/* ********************************************************
*                                                         *
* VALIDATATION HELPERS                                    *                              
* --------------------                                    * 
* - Required Fields                                       *
* - Decimal Values                                        *
* - Asset Number                                          *
* - Build Year                                            *
* - Date and DateTime                                     *
* - Greater than 0                                        *
* - Start Date < End Date                                 *
* - Validate meter readings (backend validation)          *
* - Validate department selection                         *
*                                                         *
***********************************************************/

import { useCallback, useState } from 'react';
import { validateMeterReading as validateMeterReadingAPI } from '../api/meter-readingsAPI';

export const renderErrorDot = (has) =>
  has ? (
    <span
      className="ms-6 rounded-circle bg-alert"
      style={{ position: "relative", top: "-1px", width: 8, height: 8 }}
    ></span>
  ) : null;

export const validateFieldLength = (value, maxLength, label = 'Field') => {
  if (typeof maxLength !== 'number' || maxLength <= 0) {
    return '';
  }

  const normalizedValue = typeof value === 'string' ? value.trim() : value;

  if (normalizedValue === undefined || normalizedValue === null) {
    return '';
  }

  const stringValue = String(normalizedValue);

  if (stringValue.length > maxLength) {
    return `${label} must be ${maxLength} characters or fewer`;
  }

  return '';
};

export const validateForm = (inputFields, formData) => {
  const fields = Array.isArray(inputFields) ? inputFields.flat() : [];
  const newErrors = {};

  // Global max / min date entry
  const minDate = new Date('1950-01-01');
  const maxDate = new Date('2075-12-31');

  fields.forEach(({
    field,
    label,
    required,
    type,
    mustBeGreaterThanZero,
    mustBePositiveInteger,
    mustBePositiveDecimal,
    maxDecimalPlaces,
    noFutureDate,
    allowZeroValue,
    mustBeAfterField,
    mustBeAfterFieldMessage,
    maxLength
  }) => {
    const value = formData[field];

    // Validate required fields
    if (required) {
      if (Array.isArray(value) && value.length === 0) {
        newErrors[field] = `${label} is required`;
      } else if (value === undefined || value === null || value === '') {
        newErrors[field] = `${label} is required`;
      } else if (typeof value === 'string' && value.trim() === '') {
        newErrors[field] = `${label} is required`;
      }
    }

    // Validate asset number
    if (field === 'assetNumber' && typeof value === 'string' && /\s/.test(value)) {
      newErrors[field] = `${label} cannot contain spaces`;
    }

    // Validate decimal fields
    const decimalFields = [
      'labourHours',
      'partsCost',
      'miscCost',
      'frequency',
      'purchasePrice',
      'leaseInterestRate',
      'leasePayments',
      'depnRate',
    ];

    if (decimalFields.includes(field)) {
      if (value < 0) {
        newErrors[field] = `${label} cannot be negative`;
      } else if (value && !/^\d+(\.\d{1,2})?$/.test(String(value))) {
        newErrors[field] = `${label} must be a valid number with up to 2 decimal places`;
      }
    }

    if (mustBePositiveDecimal) {
      const decimalLimit = maxDecimalPlaces && Number.isInteger(maxDecimalPlaces)
        ? maxDecimalPlaces
        : 2;

      if (value !== undefined && value !== null && value !== '') {
        const pattern = new RegExp(`^\\d+(?:\\.\\d{1,${decimalLimit}})?$`);
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue) || numericValue < 0) {
          newErrors[field] = `${label} must be greater than zero`;
        } else if (!pattern.test(String(value))) {
          newErrors[field] = `${label} must be a valid number with up to ${decimalLimit} decimal places`;
        }
      }
    }

    // Validate build year
    if (field === 'buildYear' && value !== undefined && value !== null && value !== '') {
      if (!/^(19|20)\d{2}$/.test(String(value))) {
        newErrors[field] = `${label} must be a valid four-digit year`;
      }
    }

    if (type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(String(value))) {
        newErrors[field] = `${label} must be a valid email`;
      }
    }

    // Validate date and datetime
    const isDateField =
      type === 'date' ||
      type === 'datetime';
      // || (typeof field === 'string' && field.toLowerCase().includes('date'));

    if (isDateField && value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        newErrors[field] = `${label} must be a valid date`;
      } else if (date < minDate || date > maxDate) {
        newErrors[field] = `${label} must be between 01/01/1900 and 31/12/2100`;
      } else if (noFutureDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        if (date > today) {
          newErrors[field] = `${label} cannot be in the future`;
        }
      }
    }

    // Validate must be greater than zero
    if (mustBeGreaterThanZero) {
      if (value !== undefined && value !== null && value !== '') {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue) || numericValue <= 0) {
          newErrors[field] = `${label} must be greater than zero`;
        }
      }
    }

    if (mustBePositiveInteger) {
      if (value !== undefined && value !== null && value !== '') {
        const numericValue = Number(value);
        const minAllowed = allowZeroValue ? 0 : 1;
        const messageSuffix = allowZeroValue ? 'zero or a positive integer' : 'a positive integer';

        if (!Number.isInteger(numericValue) || numericValue < minAllowed) {
          newErrors[field] = `${label} must be ${messageSuffix}`;
        }
      }
    }

    if (mustBeAfterField && value && formData[mustBeAfterField]) {
      const startDate = new Date(formData[mustBeAfterField]);
      const endDate = new Date(value);

      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate < startDate) {
        newErrors[field] =
          mustBeAfterFieldMessage || `${label} cannot be before ${String(mustBeAfterField)}`;
      }
    }

    const lengthError = validateFieldLength(value, maxLength, label);
    
    if (lengthError) {
      newErrors[field] = lengthError;
    }
  });

  //  Validate start date < end date
  if (!newErrors.assetEndDate && formData.assetStartDate && formData.assetEndDate) {
    const start = new Date(formData.assetStartDate);
    const end = new Date(formData.assetEndDate);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start) {
      newErrors.assetEndDate = 'Asset endDate must be after the startDate';
    }
  }

  if (formData.leased && formData.leaseStartDate && formData.leaseEndDate) {
    const start = new Date(formData.leaseStartDate);
    const end = new Date(formData.leaseEndDate);
    if (end <= start) {
      newErrors.leaseEndDate = 'Lease End Date must be after start date';
    }
  }

  return newErrors;
};

// Metre Readings
export const validateMeterReading = async (payload = {}) => {
  const response = await validateMeterReadingAPI(payload);
  const errors = {};
  if (response?.dateError) {
    errors.readingDate = response.dateError;
  }
  if (response?.valueError) {
    errors.value = response.valueError;
  }
  return { isValid: !response?.hasErrors, errors, response };
};

const NAME_REGEX = /^[a-zA-Z0-9\s]+$/;

export const useValidator = (inputFields, formData) => {
  const [errors, setErrors] = useState({});

  const validate = useCallback(() => {
    if (!Array.isArray(inputFields) || inputFields.length === 0) {
      return true;
    }
    const newErrors = validateForm(inputFields, formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, inputFields]);

  const validateName = useCallback((value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    let error = '';

    if (!trimmed) {
      error = 'Name is required';
    } else if (trimmed.length < 3) {
      error = 'Name must be at least 3 characters';
    } else if (!NAME_REGEX.test(trimmed)) {
      error = 'Name cannot contain special characters';
    }

    setErrors((prev) => ({ ...prev, name: error }));
    return !error;
  }, []);

  // Validate department selection
  const validateDepartmentsSelection = useCallback((values = {}) => {
    const isAllDepartments =
      values?.allDepartments === true ||
      values?.allDepartments === 1 ||
      values?.allDepartments === '1';
    const departments = Array.isArray(values?.departments)
      ? values.departments.filter((dept) => dept !== null && dept !== undefined)
      : [];
    let error = '';

    if (!isAllDepartments && departments.length === 0) {
      error = 'Select at least one department';
    }

    setErrors((prev) => {
      if (!error && !prev.departments) {
        return prev;
      }

      const next = { ...prev };
      if (error) {
        next.departments = error;
      } else {
        delete next.departments;
      }
      return next;
    });

    return !error;
  }, []);

  const validateAll = useCallback(
    (values = {}) => {
      let result = true;

      if (Object.prototype.hasOwnProperty.call(values, 'name')) {
        result = validateName(values?.name) && result;
      }

      if (
        Object.prototype.hasOwnProperty.call(values, 'allDepartments') ||
        Object.prototype.hasOwnProperty.call(values, 'departments')
      ) {
        result = validateDepartmentsSelection(values) && result;
      }

      return result;
    },
    [validateName, validateDepartmentsSelection],
  );

  // Reset errors after validation

  const resetErrors = useCallback(() => setErrors({}), []);

  return {
    errors,
    setErrors,
    validate,
    validateName,
    validateDepartmentsSelection,
    validateAll,
    resetErrors,
  };
};

